import { AccountInfo, AuthenticationResult, EventMessage, EventType, InteractionRequiredAuthError, IPublicClientApplication, PublicClientApplication } from '@azure/msal-browser';
import { msalConfig } from "./authConfig";
import { predefinedKeys } from '../components/LocalStorageProcesser';

// Initialize a variable to hold the MSAL instance
export const msalInstance = new PublicClientApplication(msalConfig);

// Asynchronously initialize the MSAL instance if not already initialized
export async function initializeMsal() {
  await msalInstance.initialize();

  // Wait for redirect to finish so we do not attempt to render a page using the redirect link
  await msalInstance.handleRedirectPromise();

  msalInstance.addEventCallback((event: EventMessage) => {
    if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
      const payload = event.payload as AuthenticationResult;
      msalInstance.setActiveAccount(payload.account);
    }
  });

  const accounts = msalInstance.getAllAccounts();
  if (accounts.length > 0 && !msalInstance.getActiveAccount()) {
    msalInstance.setActiveAccount(accounts[0]);
  }
}

// Define a Fetcher type for making authenticated API calls
export type Fetcher = {
  fetch(url: RequestInfo, init?: RequestInit | undefined): Promise<Response>;
};

export function createDynamicFetcher(instance: IPublicClientApplication, account: AccountInfo): Fetcher {
  return {
    fetch: (url: RequestInfo, init?: RequestInit) => {
      return instance.acquireTokenSilent({
        scopes: ["openid", "profile", "email"],
        account: account
      })
      .then((response) => {
        const accessToken = response.idToken;
        const requestInit = init ?? {};
        const headers = new Headers(requestInit.headers);

        if (accessToken) {
          headers.set("Authorization", `Bearer ${accessToken}`);
        }
        requestInit.headers = headers;

        return window.fetch(url, requestInit).then((apiResponse) => {
          if (apiResponse.status === 401) {
            console.warn("Backend rejected toekn. Re-authenticating");

            clearStaleAuthStorage();
            instance.loginRedirect();

            throw new Error("Session expired. Redirecting...");
          }

          return apiResponse;
        });
      })
      .catch((error) => {
        const errorName = error?.name || "";
        const errorMessage = error.message || "";

        const isSessionRevoked =
          error instanceof InteractionRequiredAuthError ||
          errorName.includes("AuthError") ||
          errorName.includes("InteractionRequired") ||
          errorMessage.includes("invalid_grant") ||
          errorMessage.includes("interaction_required") ||
          errorMessage.includes("login_required");

        if (isSessionRevoked) {
          console.warn("Session revoked or expired. Forcing re-authentication.", error);

          clearStaleAuthStorage();
          instance.loginRedirect();

          throw new Error("Session revoked. Redirecting...");
        }

        throw error;
      });
    },
  };
}
/**
 *  Clears all local storage keys except for the ones explicitly defined and preserved in predefinedKeys.
 *  This prevents MSAL infinite redirect loops caused by stale auth data.
 */
export function clearStaleAuthStorage(): void {
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && !(Object.values(predefinedKeys) as string[]).includes(key)) {
      localStorage.removeItem(key);
      i--; // Important: adjust index because localStorage.length decreases
    }
  }
}